import { Box, Button, MenuItem, styled, TextField, Typography, TextareaAutosize } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import axios from 'axios'
import { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import InterviewCategoryCard from '@components/InterviewCategoryCard/InterviewCategoryCard'
import userInviteStore from 'store/userInviteStore'

export const Route = createFileRoute(
  '/(partner)/_partner/interview-from-scratch',
)({
  component: RouteComponent,
})


interface CreateInterviewDto {
  technologyId: string;
  difficulty: string;
  readTime: string;
  answerTime: string;
  description: string;
  name: string;
}

const StyledTextArea = styled(TextareaAutosize)({
    backgroundColor: '#F9F9F9',
    width: '100%',
    minHeight: '135px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid var(--Interviewer-Lightest-Black)',
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: '20px',
    resize: 'vertical',
    '&:focus': {
    outline: 'none',
    },
})

const StyledInterviewContent = styled(Box)({
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#fff',
    padding: '20px',
    border: '1px solid var(--Light-Gray)',
    borderRadius: '12px',
    boxShadow: '0px 1px 2px 0px #0205111A'
})


function RouteComponent() {

  const navigate = useNavigate()
  const queryClient = useQueryClient();

  
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedTechnology, setSelectedTechnology] = useState('')
  const [selectedDifficulty, setSelectedDifficulty] = useState('')


  const [isCreatedInterview, setIsCreatedInterview] = useState(false)
  const [idOfCreatedInterview, setIdOfCreatedInterview] = useState<string | null>(null);

  const {userData, updateUserData} = userInviteStore();

  const [errors, setErrors] = useState({
    name: "",
    // description: "",
    difficulty: "",
    technology: ""
  });

  // Postojece tehnologije
  const { data: technologies, isLoading: isLoadingTechnologies, error: technologiesError } = useQuery({
    queryKey: ['technologies', 'create-interview'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://interviewer.runasp.net/api/technologies', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
  });


  // Prikupljanje kategorija
  const { data: categories, isLoading: isLoadingCategories, error: categoriesError } = useQuery({
    queryKey: ['categories', 'create-interview'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://interviewer.runasp.net/api/categories', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log('kategorije', response);
      return response.data;
    },
  });

  //Validacija polja
  const validate = () => {
    let valid = true
    const newErrors = { name: "", difficulty: "", technology: ""};

    if(name.trim().length < 1){
      newErrors.name = 'Please add name';
      valid = false;
    }

    // if(description.trim().length < 1){
    //   newErrors.description = 'Please add description';
    //   valid = false;
    // }

    if(selectedDifficulty.trim().length < 1){
      newErrors.difficulty = 'Please add difficulty';
      valid = false;
    }
    
    if(selectedTechnology.trim().length < 1){
      newErrors.technology = 'Please add technology';
      valid = false;
    }

    setErrors(newErrors);
    return valid
  }

  // Kreiranje intervjua ili prelazak na sledecu stranicu 'duration'
  const handleCreateInterview = () => {
    if (!validate()) return;

    if (isCreatedInterview) {
      console.log('idemo na duration');
      navigate({
        to: '/interview-duration'
      })
    } else {
      console.log('kreiramo');
      const interviewData = {
          technologyId: selectedTechnology,
          difficulty: selectedDifficulty,
          readTime: "200",
          answerTime: "300",
          description: description,
          name: name,
      };
      createInterviewMutation.mutate(interviewData);
    }
  };

  const createInterviewMutation = useMutation({
    mutationFn: async (interviewData: CreateInterviewDto) => {
      const token = localStorage.getItem('token');
      const response = await axios.post('https://interviewer.runasp.net/api/interviews', interviewData, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      return response;
    },
    onSuccess: (data) => {
      // console.log('kreiran interview', data);
      updateUserData({
        interviewId: data.data
      })
      setIdOfCreatedInterview(data.data);
      setIsCreatedInterview(true);
      toast.success('Successfully created interview!')
    },
    onError: (error) => {
      console.log(error);
    }
  });

  //Pribavljanje kontenta
  const {data: interviewContent} = useQuery({
    queryKey: ['newtest'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://interviewer.runasp.net/api/interviews/${idOfCreatedInterview}/content`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      })
      console.log('pribavljanje Kontenta', response);
      return response.data;
    },
    enabled: !!idOfCreatedInterview
  }) 

  // Povezivanje kategorije na kreirani intervju
  const addCategoryToInterviewMutation = useMutation({
    mutationKey: ['test'],
    mutationFn: async (categoryId) => {
      const token = localStorage.getItem('token');
      const response = await axios.put(`https://interviewer.runasp.net/api/interviews/${idOfCreatedInterview}/link-category`,
        {categoryId: categoryId}, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        })
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['newtest'] });
      console.log(data);
    },
    onError: (error) => {
      console.log(error);
    }
  })

  return (
    <Box>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: {xs: '20px', md: '48px'}, mb: '64px'}}>
        <TextField 
          label="Interview Name"
          variant="outlined"
          fullWidth
          value={name}
          onChange={e => setName(e.target.value)}
          error={!!errors.name}
          // helperText={errors.name}
          // disabled={interviewCreated && !interviewId}
        />
        <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: '20px'}}>
          <TextField 
            label="Select a technology"
            variant="outlined"
            fullWidth
            select
            value={selectedTechnology}
            onChange={(e) => setSelectedTechnology(e.target.value)}
            error={!!errors.technology}
          >
            {technologies?.map((technology: any) => (
              <MenuItem key={technology.id} value={technology.id}>
                {technology.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField 
            label="Select a difficulty"
            variant="outlined"
            fullWidth
            select
            value={selectedDifficulty}
            onChange={(e) => setSelectedDifficulty(e.target.value)}
            error={!!errors.difficulty}
          >
            <MenuItem value="entry">Entry</MenuItem>
            <MenuItem value="advanced">Advanced</MenuItem>
            <MenuItem value="professional">Professional</MenuItem>
          </TextField>
        </Box>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          <StyledTextArea
            placeholder='Short Interview Description ...'
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end'}}>
          <Button 
            variant='contained' 
            sx={{minWidth: '180px'}}
            onClick={handleCreateInterview}
          >
            {isCreatedInterview ? 'Next' : 'Publish Interview'}
          </Button>
        </Box>
      </Box>
      
      {(isCreatedInterview) && (
        <Box sx={{display: 'flex', flexDirection: {xs: 'column', md: 'row'}, gap: '20px'}}>
            <Box sx={{width: '100%'}}>
                <Typography variant='h2' sx={{pb: '16px'}}>Add a category</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px'}}>
                    {categories?.map((category: any) => (
                        <InterviewCategoryCard
                          id={category.id}
                          name={category.name} 
                          description={category.description} 
                          onAdd={() => addCategoryToInterviewMutation.mutate(category.id)}/>
                    ))}
                </Box>
            </Box>
            <Box sx={{width: '100%'}}>
                <Typography variant='h2' sx={{pb: '16px'}}>Interview Content</Typography>
                <StyledInterviewContent>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "24px", width: "100%" }}>
                    {interviewContent?.categories?.map((category) => (
                      <Box 
                        key={category.id} 
                        sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
                      >
                        <Typography variant="h2" sx={{fontSize: '16px'}}>{category.name}</Typography>

                        {/* Questions list */}
                        <Box sx={{ pl: 3, display: "flex", flexDirection: "column", gap: "12px" }}>
                          {category.questions.map((question, index) => (
                            <Typography key={index} sx={{ fontSize: "14px", color: 'var(--Interviewer-Black)' }}>
                              <strong>Question {index + 1}:</strong> {question.text}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </StyledInterviewContent>
            </Box>
        </Box>
      )}
    </Box>
  )
}
